/**
 * @file ContentArticleEditPage.tsx
 * @description Редактор статьи блога (phase12.0) — Tiptap + SEO + meta.
 *
 * URL: `/shop/:slug/content/articles/new` (создание) или `/.../:id` (редактирование).
 */

import { useEffect, useMemo, useState } from "react";
import { Link, useNavigate, useParams } from "react-router";

import { SeoChecklist } from "../../../components/content/SeoChecklist";
import { SerpPreview } from "../../../components/content/SerpPreview";
import { TiptapEditor } from "../../../components/content/TiptapEditor";
import { Alert } from "../../../components/ui/alert";
import { Button } from "../../../components/ui/button";
import { EditorialCard } from "../../../components/ui/EditorialCard";
import { Input } from "../../../components/ui/input";
import { useNotify } from "../../../components/ui/NotifyProvider";
import { ApiError } from "../../../lib/api-client";
import {
  contentArticles,
  contentAuthors,
  contentCategories,
  contentTags,
  type ArticleDetail,
  type ContentAuthor,
  type ContentCategory,
  type ContentTag,
} from "../../../lib/api-content";
import { RedirectOldSlugCheck } from "../../../components/site-seo/RedirectOldSlugCheck";
import { SeoKeywordsField } from "../../../components/site-seo/SeoKeywordsField";
import { EMPTY_SEO_KEYWORDS, keywordsInput, keywordsOf, type SeoKeywordsValue } from "../../../lib/site-seo/keywords";
import { notifyRedirectChange } from "../../../lib/site-seo/redirect-change";

const PUBLIC_SITE_URL =
  (typeof window !== "undefined" && window.location.origin.replace("/admin", "")) ||
  "https://chaiberi.ru";

interface FormState {
  title: string;
  excerpt: string;
  slug: string;
  authorId: string;
  categoryId: string;
  tagIds: string[];
  coverAlt: string;
  coverMediaId: string;
  bodyJson: Record<string, unknown> | null;
  bodyHtml: string;
  seoTitle: string;
  seoDescription: string;
  canonicalUrl: string;
  noIndex: boolean;
  /** Р3в (SEO11): ключевые запросы — заметка, на сайт не выводится. */
  keywords: SeoKeywordsValue;
  /** Р3в (SB131): переадресовать со старого адреса при смене адреса. */
  redirectFromOldSlug: boolean;
}

const EMPTY: FormState = {
  title: "",
  excerpt: "",
  slug: "",
  authorId: "",
  categoryId: "",
  tagIds: [],
  coverAlt: "",
  coverMediaId: "",
  bodyJson: null,
  bodyHtml: "",
  seoTitle: "",
  seoDescription: "",
  canonicalUrl: "",
  noIndex: false,
  keywords: EMPTY_SEO_KEYWORDS,
  redirectFromOldSlug: true,
};

export function ContentArticleEditPage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const notify = useNotify();
  const isNew = !id || id === "new";

  const [form, setForm] = useState<FormState>(EMPTY);
  const [article, setArticle] = useState<ArticleDetail | null>(null);
  const [authors, setAuthors] = useState<ContentAuthor[]>([]);
  const [categories, setCategories] = useState<ContentCategory[]>([]);
  const [allTags, setAllTags] = useState<ContentTag[]>([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    async function load() {
      setLoading(true);
      try {
        const [a, c, t, art] = await Promise.all([
          contentAuthors.list(),
          contentCategories.list(),
          contentTags.list(),
          isNew ? Promise.resolve(null) : contentArticles.findOne(id!),
        ]);
        setAuthors(a);
        setCategories(c);
        setAllTags(t);
        if (art) {
          setArticle(art);
          setForm({
            title: art.title,
            excerpt: art.excerpt,
            slug: art.slug,
            authorId: art.authorId,
            categoryId: art.categoryId,
            tagIds: t.filter((tag) => art.tagSlugs.includes(tag.slug)).map((tag) => tag.id),
            coverAlt: art.coverAlt,
            coverMediaId: art.coverMediaId ?? "",
            bodyJson: art.bodyJson as Record<string, unknown>,
            bodyHtml: art.bodyHtml,
            seoTitle: art.seoTitle ?? "",
            seoDescription: art.seoDescription ?? "",
            canonicalUrl: art.canonicalUrl ?? "",
            noIndex: art.noIndex,
            keywords: keywordsOf(art),
            redirectFromOldSlug: true,
          });
        } else {
          setForm({
            ...EMPTY,
            authorId: a[0]?.id ?? "",
            categoryId: c[0]?.id ?? "",
          });
        }
      } catch (err) {
        setError(err instanceof ApiError ? err.message : "Не удалось загрузить");
      } finally {
        setLoading(false);
      }
    }
    void load();
  }, [id, isNew]);

  const canSave = useMemo(
    () =>
      form.title.trim().length > 0 &&
      form.excerpt.trim().length > 0 &&
      form.authorId &&
      form.categoryId &&
      form.bodyHtml.length > 0,
    [form],
  );

  function update<K extends keyof FormState>(key: K, value: FormState[K]) {
    setForm((prev) => ({ ...prev, [key]: value }));
  }

  function toggleTag(tagId: string) {
    setForm((prev) => ({
      ...prev,
      tagIds: prev.tagIds.includes(tagId)
        ? prev.tagIds.filter((t) => t !== tagId)
        : [...prev.tagIds, tagId],
    }));
  }

  async function onSave(action: "save" | "publish" | "unpublish" | "archive") {
    setSaving(true);
    try {
      const payload = {
        title: form.title.trim(),
        excerpt: form.excerpt.trim(),
        slug: form.slug.trim() || undefined,
        authorId: form.authorId,
        categoryId: form.categoryId,
        tagIds: form.tagIds,
        coverAlt: form.coverAlt,
        coverMediaId: form.coverMediaId || undefined,
        bodyJson: form.bodyJson ?? { type: "doc", content: [] },
        bodyHtml: form.bodyHtml,
        seoTitle: form.seoTitle || undefined,
        seoDescription: form.seoDescription || undefined,
        canonicalUrl: form.canonicalUrl || undefined,
        noIndex: form.noIndex,
        ...keywordsInput(form.keywords),
        ...(article && form.slug.trim() && form.slug.trim() !== article.slug ? { redirectFromOldSlug: form.redirectFromOldSlug } : {}),
      };
      const saved = isNew
        ? await contentArticles.create(payload)
        : await contentArticles.update(id!, payload);

      let final = saved;
      if (action === "publish") final = await contentArticles.publish(saved.id);
      else if (action === "unpublish") final = await contentArticles.unpublish(saved.id);
      else if (action === "archive") final = await contentArticles.archive(saved.id);

      notify.success(
        action === "publish"
          ? "Опубликовано"
          : action === "unpublish"
            ? "Снято с публикации"
            : action === "archive"
              ? "В архиве"
              : "Сохранено",
      );
      setArticle(final);
      // Р3в (SB131): переадресация со старого адреса создана или снята — тостом.
      notifyRedirectChange(saved.redirect, notify);
      if (isNew) navigate(`../${final.id}`, { relative: "path" });
    } catch (err) {
      notify.error(err instanceof ApiError ? err.message : "Не удалось сохранить");
    } finally {
      setSaving(false);
    }
  }

  if (loading) {
    return <p className="text-sm text-on-surface-muted">Загрузка…</p>;
  }

  return (
    <div className="flex flex-col gap-6">
      <div className="flex items-start justify-between gap-4 flex-wrap">
        <div>
          <div className="label-editorial">
            <Link to=".." relative="path" className="hover:underline">
              Статьи
            </Link>{" "}
            · {isNew ? "Новая" : "Редактирование"}
          </div>
          <h1 className="text-3xl font-semibold tracking-tight mt-1">
            {form.title || "Без названия"}
          </h1>
          {article && (
            <div className="text-xs text-on-surface-muted mt-1">
              Статус: {article.status} · slug: <code>{article.slug}</code>
            </div>
          )}
        </div>
        <div className="flex flex-wrap gap-2">
          <Button
            variant="secondary"
            onClick={() => void onSave("save")}
            disabled={!canSave || saving}
          >
            Сохранить
          </Button>
          {article?.status === "PUBLISHED" ? (
            <>
              <Button
                variant="secondary"
                onClick={() => void onSave("unpublish")}
                disabled={saving}
              >
                Снять с публикации
              </Button>
              <Button
                variant="secondary"
                onClick={() => void onSave("archive")}
                disabled={saving}
              >
                В архив
              </Button>
            </>
          ) : (
            <Button onClick={() => void onSave("publish")} disabled={!canSave || saving}>
              Опубликовать
            </Button>
          )}
        </div>
      </div>

      {error ? <Alert severity="error">{error}</Alert> : null}

      <EditorialCard>
        <EditorialCard.Body>
          <div className="flex flex-col gap-3">
            <Input
              placeholder="Заголовок статьи"
              value={form.title}
              onChange={(e) => update("title", e.target.value)}
              className="text-2xl font-semibold"
            />
            <Input
              placeholder="Краткое описание (excerpt) — 1-2 предложения"
              value={form.excerpt}
              onChange={(e) => update("excerpt", e.target.value)}
            />
            <Input
              placeholder="Slug (URL) — оставьте пустым для авто"
              value={form.slug}
              onChange={(e) => update("slug", e.target.value)}
            />
            <RedirectOldSlugCheck
              oldPath={`/blog/${article?.slug ?? ""}`}
              changed={!!article && form.slug.trim() !== "" && form.slug.trim() !== article.slug}
              checked={form.redirectFromOldSlug}
              onChange={(value) => update("redirectFromOldSlug", value)}
            />
          </div>
        </EditorialCard.Body>
      </EditorialCard>

      <div className="grid grid-cols-1 lg:grid-cols-[320px_minmax(0,1fr)_320px] gap-6">
        <div className="flex flex-col gap-6 order-2 lg:order-1">
          <EditorialCard>
            <EditorialCard.Body>
              <h3 className="text-sm font-semibold mb-3">SEO-настройки</h3>
              <div className="flex flex-col gap-3">
                <div>
                  <label className="text-xs text-on-surface-muted mb-1 block">
                    Meta title (override)
                  </label>
                  <Input
                    placeholder={form.title}
                    value={form.seoTitle}
                    onChange={(e) => update("seoTitle", e.target.value)}
                  />
                </div>
                <div>
                  <label className="text-xs text-on-surface-muted mb-1 block">
                    Meta description (override)
                  </label>
                  <Input
                    placeholder={form.excerpt}
                    value={form.seoDescription}
                    onChange={(e) => update("seoDescription", e.target.value)}
                  />
                </div>
                <div>
                  <label className="text-xs text-on-surface-muted mb-1 block">
                    Canonical URL (опц.)
                  </label>
                  <Input
                    placeholder={`${PUBLIC_SITE_URL}/blog/${form.slug || "..."}`}
                    value={form.canonicalUrl}
                    onChange={(e) => update("canonicalUrl", e.target.value)}
                  />
                </div>
                <label className="flex items-center gap-2 text-sm">
                  <input
                    type="checkbox"
                    checked={form.noIndex}
                    onChange={(e) => update("noIndex", e.target.checked)}
                  />
                  noindex — не индексировать в поисковиках
                </label>
              </div>
              <div className="mt-4">
                <SeoKeywordsField
                  value={form.keywords}
                  onChange={(value) => update("keywords", value)}
                  title={form.seoTitle || form.title}
                  h1={form.title}
                  conflicts={article?.seoKeywordConflicts ?? []}
                />
              </div>
              <div className="mt-4">
                <SerpPreview
                  baseUrl={PUBLIC_SITE_URL}
                  slug={form.slug}
                  title={form.seoTitle || form.title}
                  description={form.seoDescription || form.excerpt}
                />
              </div>
            </EditorialCard.Body>
          </EditorialCard>
        </div>

        <div className="flex flex-col gap-6 min-w-0 order-1 lg:order-2">
          <EditorialCard className="lg:flex lg:flex-1 lg:flex-col">
            <EditorialCard.Body className="lg:flex lg:flex-1 lg:flex-col">
              <div className="mb-2 text-sm font-semibold">Тело статьи</div>
              <TiptapEditor
                value={form.bodyJson}
                onChange={(json, html) => {
                  setForm((prev) => ({ ...prev, bodyJson: json, bodyHtml: html }));
                }}
                placeholder="Начните писать…"
                className="lg:flex-1 lg:min-h-[400px]"
              />
            </EditorialCard.Body>
          </EditorialCard>
        </div>

        <div className="flex flex-col gap-6 order-3 lg:order-3">
          <EditorialCard>
            <EditorialCard.Body>
              <h3 className="text-sm font-semibold mb-3">Категория и автор</h3>
              <div className="flex flex-col gap-3">
                <label className="flex flex-col gap-1">
                  <span className="text-xs text-on-surface-muted">Категория</span>
                  <select
                    value={form.categoryId}
                    onChange={(e) => update("categoryId", e.target.value)}
                    className="rounded-md border border-on-surface/20 bg-surface px-3 py-2 text-sm"
                  >
                    <option value="">— Выберите —</option>
                    {categories.map((c) => (
                      <option key={c.id} value={c.id}>
                        {c.name}
                      </option>
                    ))}
                  </select>
                </label>
                <label className="flex flex-col gap-1">
                  <span className="text-xs text-on-surface-muted">Автор</span>
                  <select
                    value={form.authorId}
                    onChange={(e) => update("authorId", e.target.value)}
                    className="rounded-md border border-on-surface/20 bg-surface px-3 py-2 text-sm"
                  >
                    <option value="">— Выберите —</option>
                    {authors.map((a) => (
                      <option key={a.id} value={a.id}>
                        {a.name}
                      </option>
                    ))}
                  </select>
                </label>
              </div>
            </EditorialCard.Body>
          </EditorialCard>

          <EditorialCard>
            <EditorialCard.Body>
              <h3 className="text-sm font-semibold mb-3">Теги</h3>
              {allTags.length === 0 ? (
                <p className="text-sm text-on-surface-muted">Создайте теги в разделе «Теги».</p>
              ) : (
                <div className="flex flex-wrap gap-1.5">
                  {allTags.map((t) => {
                    const selected = form.tagIds.includes(t.id);
                    return (
                      <button
                        key={t.id}
                        type="button"
                        onClick={() => toggleTag(t.id)}
                        className={`rounded-full px-3 py-1 text-sm font-medium transition-colors ${
                          selected
                            ? "bg-primary text-white"
                            : "bg-on-surface/8 text-on-surface hover:bg-on-surface/15"
                        }`}
                      >
                        {t.name}
                      </button>
                    );
                  })}
                </div>
              )}
            </EditorialCard.Body>
          </EditorialCard>

          <EditorialCard>
            <EditorialCard.Body>
              <h3 className="text-sm font-semibold mb-3">Обложка</h3>
              <Input
                placeholder="Alt-текст обложки (обязательно)"
                value={form.coverAlt}
                onChange={(e) => update("coverAlt", e.target.value)}
              />
              <Input
                placeholder="MediaAssetId (опц., MediaPicker — позже)"
                value={form.coverMediaId}
                onChange={(e) => update("coverMediaId", e.target.value)}
                className="mt-2"
              />
            </EditorialCard.Body>
          </EditorialCard>

          <SeoChecklist
            title={form.seoTitle || form.title}
            description={form.seoDescription || form.excerpt}
            coverAlt={form.coverAlt}
            bodyHtml={form.bodyHtml}
            noIndex={form.noIndex}
          />
        </div>
      </div>
    </div>
  );
}
